import React, { useState, useEffect, useRef, useCallback } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const FONT_OPTIONS = [
  { label: "System default", value: "inherit" },
  { label: "Serif (Georgia)", value: "Georgia, 'Times New Roman', serif" },
  { label: "Sans-serif (Helvetica)", value: "Helvetica, Arial, sans-serif" },
  { label: "Monospace (Courier)", value: "'Courier New', Courier, monospace" },
];

const FONT_SIZE_MIN = 12;
const FONT_SIZE_MAX = 28;
const FONT_SIZE_STEP = 2;

const NavSettingsDropdown = ({ isAuthenticated, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Font size
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('i7_font_size');
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(parsed) && parsed >= FONT_SIZE_MIN && parsed <= FONT_SIZE_MAX
      ? parsed
      : 16;
  });

  // Font family
  const [fontFamily, setFontFamily] = useState(() => {
    const saved = localStorage.getItem('i7_font_family');
    return saved && FONT_OPTIONS.some((o) => o.value === saved) ? saved : 'inherit';
  });

  // Dark mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('i7-dark-mode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply font size globally
  useEffect(() => {
    document.documentElement.style.setProperty('--user-font-size', `${fontSize}px`);
    localStorage.setItem('i7_font_size', String(fontSize));
  }, [fontSize]);

  // Apply font family globally
  useEffect(() => {
    const val = fontFamily === 'inherit' ? 'var(--font-sans)' : fontFamily;
    document.documentElement.style.setProperty('--user-font-family', val);
    localStorage.setItem('i7_font_family', fontFamily);
  }, [fontFamily]);

  // Apply dark mode
  useEffect(() => {
    localStorage.setItem('i7-dark-mode', JSON.stringify(isDarkMode));
    document.documentElement.classList.toggle('dark-theme', isDarkMode);
  }, [isDarkMode]);

  // Close on click outside or Escape
  const handleClickOutside = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleClickOutside, handleKeyDown]);

  const handleDecrease = () => {
    setFontSize((prev) => Math.max(FONT_SIZE_MIN, prev - FONT_SIZE_STEP));
  };

  const handleIncrease = () => {
    setFontSize((prev) => Math.min(FONT_SIZE_MAX, prev + FONT_SIZE_STEP));
  };

  return (
    <div className="nav-settings-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="nav-settings-trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Site settings"
        aria-expanded={open}
      >
        <SettingsIcon style={{ fontSize: 22 }} />
      </button>

      {open && (
        <div className="nav-settings-panel">
          {/* Font size */}
          <div className="nav-settings-section">
            <span className="nav-settings-label">Font size</span>
            <div className="nav-settings-row">
              <button
                type="button"
                onClick={handleDecrease}
                disabled={fontSize <= FONT_SIZE_MIN}
                aria-label="Decrease font size"
                className="nav-settings-btn"
              >
                <RemoveIcon style={{ fontSize: 16 }} />
              </button>
              <span className="nav-settings-value">{fontSize}px</span>
              <button
                type="button"
                onClick={handleIncrease}
                disabled={fontSize >= FONT_SIZE_MAX}
                aria-label="Increase font size"
                className="nav-settings-btn"
              >
                <AddIcon style={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* Font family */}
          <div className="nav-settings-section">
            <span className="nav-settings-label">Font</span>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              aria-label="Font family"
              className="nav-settings-select"
            >
              {FONT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Theme toggle */}
          <div className="nav-settings-section">
            <span className="nav-settings-label">Theme</span>
            <button
              type="button"
              onClick={() => setIsDarkMode((prev) => !prev)}
              className="theme-toggle"
              aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
            >
              <div className="theme-toggle-track">
                <div className={`theme-toggle-thumb ${isDarkMode ? 'dark' : 'light'}`}>
                  <span className="theme-toggle-icon">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      {isDarkMode ? (
                        <circle cx="7" cy="7" r="6" fill="currentColor" stroke="none" />
                      ) : (
                        <circle cx="7" cy="7" r="6" fill="white" stroke="currentColor" strokeWidth="1" />
                      )}
                    </svg>
                  </span>
                </div>
              </div>
            </button>
          </div>

          {/* Account links */}
          {isAuthenticated && (
            <div className="nav-settings-account">
              <a href="/settings" className="nav-settings-link">Settings</a>
              <a href="/" onClick={onSignOut} className="nav-settings-link">Sign Out</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NavSettingsDropdown;
